export const getReplacement = (product) =>
	product?.replacement ||
	product?.productData?.replacement ||
	product?.productsData?.replacement ||
	(product?.replacement_product_id ? product : null);

export const getReplacedBy = (product) =>
	product?.replaced_by ||
	product?.replacedBy ||
	product?.productData?.replaced_by ||
	product?.productsData?.replaced_by ||
	null;

export const getReplacementPartNumber = (product) => {
	const replacement = getReplacement(product);
	return (
		replacement?.replacement_product_number ||
		replacement?.alternative_item_number ||
		product?.alternative_item_number ||
		product?.alternativeitemnumber ||
		product?.alternativeItemNumber ||
		""
	);
};

export const getReplacedByPartNumber = (product) => {
	const replacedBy = getReplacedBy(product);
	return (
		replacedBy?.replaced_product_number ||
		replacedBy?.product_number ||
		""
	);
};

export const hasResolvedReplacement = (product) =>
	Boolean(
		getReplacement(product)?.replacement_product_id &&
		getReplacementPartNumber(product)
	);

export const getReplacementNoticeText = (product) => {
	const replacementPartNumber = getReplacementPartNumber(product);
	const replacedPartNumber = getReplacedByPartNumber(product);

	return [
		replacementPartNumber ? `Replacement Part: ${replacementPartNumber}` : "",
		replacedPartNumber ? `Replacement for Part: ${replacedPartNumber}` : "",
	]
		.filter(Boolean)
		.join(" | ");
};

export const getEffectiveProductTarget = (
	product,
	{ machineId, currency } = {}
) => {
	const replacement = getReplacement(product);
	const normalizedCurrency = String(currency || "").trim().toLowerCase();
	const replacementPriceKey = normalizedCurrency
		? `replacement_price_${normalizedCurrency}`
		: "";

	if (!replacement?.replacement_product_id) {
		return {
			product_id: product?.product_id ?? product?.ProductID,
			product_number: product?.product_number ?? product?.ProductNumber,
			product_name: product?.product_name ?? product?.ProductName,
			machine_id:
				machineId ??
				product?.machine_id ??
				product?.MachineID ??
				product?.machineId,
			purchasable: Number(product?.purchasable) === 1,
			price: normalizedCurrency
				? Number(product?.[`price_${normalizedCurrency}`] || 0)
				: 0,
			replaced: false,
		};
	}

	return {
		product_id: replacement.replacement_product_id,
		product_number: replacement.replacement_product_number,
		product_name:
			replacement.replacement_product_name ||
			replacement.replacement_product_number,
		machine_id:
			replacement.replacement_machine_id ||
			machineId ||
			product?.machine_id ||
			product?.MachineID ||
			product?.machineId,
		purchasable: Number(replacement.replacement_purchasable) === 1,
		minimum_sales_order_quantity:
			replacement.replacement_minimum_sales_order_quantity ??
			product?.replacement_minimum_sales_order_quantity,
		multiple_sales_order_quantity:
			replacement.replacement_multiple_sales_order_quantity ??
			product?.replacement_multiple_sales_order_quantity,
		price: replacementPriceKey
			? Number(replacement?.[replacementPriceKey] || 0)
			: 0,
		line_discount_applied: replacement.replacement_line_discount_applied,
		line_discount_amount: replacement.replacement_line_discount_amount,
		replaced: true,
		original_product_id: product?.product_id ?? product?.ProductID,
	};
};

export const getEffectiveProductDisplay = (product, options = {}) => {
	const target = getEffectiveProductTarget(product, options);
	if (!target.replaced) return product;

	return {
		...product,
		product_id: target.product_id,
		ProductID: target.product_id,
		product_number: target.product_number,
		ProductNumber: target.product_number,
		product_name: target.product_name,
		ProductName: target.product_name,
		machine_id: target.machine_id,
		MachineID: target.machine_id,
		purchasable: target.purchasable ? 1 : 0,
		line_discount_applied: target.line_discount_applied,
		line_discount_amount: target.line_discount_amount,
		minimum_sales_order_quantity: target.minimum_sales_order_quantity,
		multiple_sales_order_quantity: target.multiple_sales_order_quantity,
		replaced_from_product_id: target.original_product_id,
		replaced_by: {
			product_number: product?.product_number ?? product?.ProductNumber,
		},
	};
};

/**
 * Build the product-detail route for the part that replaces `product`, or "" when the
 * replacement's product id is unknown.
 *
 * Two payload shapes exist. Fully resolved rows carry `replacement.replacement_product_id`
 * (server attachResolvedReplacements). Order-history rows skip that resolver for performance and
 * instead carry `alternative_product_id` / `alternative_machine_id` (server
 * attachAlternativeProductLinks). The machine falls back to the original product's machine because
 * /machines/:machineID/:productid needs one; the server's getSingleProductByContext then resolves
 * the replacement's own BOM machine when it has one, so the fallback is only a route placeholder.
 *
 * @param {Object} product Product row, or an order line carrying nested `productsData`.
 * @returns {string} Route like `/machines/12/345`, or "" when no target id is known.
 */
export const getReplacementProductRoute = (product) => {
	const data = product?.productsData || product?.productData || {};
	const replacement = getReplacement(product);
	const productId =
		replacement?.replacement_product_id ||
		product?.alternative_product_id ||
		data?.alternative_product_id;
	const machineId =
		replacement?.replacement_machine_id ||
		product?.alternative_machine_id ||
		data?.alternative_machine_id ||
		product?.machine_id ||
		data?.machine_id;
	return productId && machineId ? `/machines/${machineId}/${productId}` : "";
};
